'use client'

import { useTranslations } from 'next-intl'
import { ComponentProps, MouseEvent, useState } from 'react'
import { toast } from 'react-toastify'

import { cn } from '@/shared/lib'
import { Icon } from '@/shared/ui'

import { useLike } from './provider'
import { useAddToFavoriteList } from '../api/add-to-favorite-list'
import { useRemoveFromFavoriteList } from '../api/remove-from-favorite-list'

type Props = ComponentProps<'button'> & {
  roomId: number
  like?: boolean
  className?: string
  variant?: 'line' | 'fill'
  onLike?: () => void
  onUnlike?: () => void
}

export const LikeButton = ({
  roomId,
  like: likeProp = false,
  className,
  variant = 'fill',
  onLike,
  onUnlike,
  ...restProps
}: Props) => {
  const t = useTranslations('favorite_room')

  const likeState = useLike()

  const [isLiked, setIsLiked] = useState(likeState?.like || likeProp)

  const likeValue = likeState?.like ?? isLiked

  const addToFavoriteList = useAddToFavoriteList({
    onSuccess: () => {
      if (likeState) {
        likeState.toggleLike()
      } else {
        setIsLiked(true)
      }

      toast(t('toast.added'))

      onLike?.()
    },
  })

  const removeFromFavoriteList = useRemoveFromFavoriteList({
    onSuccess: () => {
      if (likeState) {
        likeState.toggleLike()
      } else {
        setIsLiked(false)
      }

      toast(t('toast.removed'))

      onUnlike?.()
    },
  })

  const handleClick = (event: MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()

    if (likeValue) {
      removeFromFavoriteList.execute(roomId)
    } else {
      addToFavoriteList.execute(roomId)
    }
  }

  return (
    <button
      className={cn(
        'group cursor-pointer rounded-sm p-2',
        'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
        className,
      )}
      onClick={handleClick}
      disabled={
        addToFavoriteList.isExecuting || removeFromFavoriteList.isExecuting
      }
      {...restProps}
    >
      {likeValue ? (
        <Icon
          name="heart"
          size={24}
          className="transition-all duration-300 [--icon-fill:var(--color-red-500)] [--icon-stroke:var(--color-red-500)] group-hover:scale-110 group-active:scale-90"
        />
      ) : (
        <Icon
          name="heart"
          size={24}
          className={cn(
            'transition-all duration-300 group-hover:scale-110 group-active:scale-90',
            variant === 'fill' &&
              '[--icon-fill:rgba(0,0,0,0.3)] [--icon-stroke:white]',
            variant === 'line' && '[--icon-fill:white] [--icon-stroke:black]',
          )}
          {...(variant === 'line' && {
            strokeWidth: 1.5,
          })}
        />
      )}
      <span className="sr-only">{t('button')}</span>
    </button>
  )
}
